import { Loader2 } from "lucide-react";
import { isMobileApp } from "common/utils/browser";
import { remoteImageStaticUrl } from "common/utils/remote-image";

type LoadingStateProps = {
  type: "generating" | "refining" | "uploading" | "empty";
  message?: string;
  submessage?: string;
  isMobile?: boolean;
};

export const LoadingState = ({
  type,
  message,
  submessage,
  isMobile = false
}: LoadingStateProps) => {
  if (type === "empty") {
    return (
      <div className="flex h-full flex-col justify-center">
        <div className="flex flex-1 flex-col justify-center">
          <div
            className={`mx-auto flex ${isMobileApp() ? "" : "h-full"} max-w-5xl flex-col justify-between text-center`}
          >
            <div className={`${isMobile ? "" : "mt-8"}`}>
              {isMobile ? (
                <h1 className="flex flex-col items-center text-[24px]">
                  <span className="font-light">Welcome to</span>
                  <span className="bg-gradient-to-br from-[#C781FF] to-[#1671FF] bg-clip-text font-bold text-transparent">
                    Jiffy Weave AI Studio
                  </span>
                </h1>
              ) : (
                <h1 className="text-[36px] font-light">
                  Welcome to{" "}
                  <span className="bg-gradient-to-br from-[#C781FF] to-[#1671FF] bg-clip-text font-bold text-transparent">
                    Jiffy Weave AI Studio
                  </span>
                </h1>
              )}

              <p
                className={`text-gray-500 ${isMobile ? "text-[14px]" : "text-[16px]"} mx-auto max-w-2xl leading-relaxed`}
              >
                WEAVE your ideas into reality—transforming them into wearable art. We make every
                image{" "}
                <span className={`${isMobile ? "block" : "inline"}`}>
                  Print Perfect™
                  {isMobile ? "." : ", so you can stay focused on what you do best: creating."}
                </span>
              </p>
            </div>

            <div
              className={`grid ${isMobile ? "mb-16 max-w-[200px] grid-cols-3 gap-2" : "max-w-2xl grid-cols-3 gap-8"} mx-auto`}
            >
              <img
                src={remoteImageStaticUrl("gen/jiffy-weaver-ai-studio-placeholder/polar_bear.png", {
                  w: 300,
                  h: 300
                })}
                alt="Polar Bear"
                className="h-full w-full rounded-lg border border-[#E5E7EB] object-cover p-[1px] shadow-lg"
              />
              <img
                src={remoteImageStaticUrl("gen/jiffy-weaver-ai-studio-placeholder/wolf.png", {
                  w: 300,
                  h: 300
                })}
                alt="Wolf"
                className="h-full w-full rounded-lg border border-[#E5E7EB] object-cover p-[1px] shadow-lg"
              />
              <img
                src={remoteImageStaticUrl("gen/jiffy-weaver-ai-studio-placeholder/astronaut.png", {
                  w: 300,
                  h: 300
                })}
                alt="Astronaut"
                className="h-full w-full rounded-lg border border-[#E5E7EB] object-cover p-[1px] shadow-lg"
              />
            </div>

            {isMobile ? null : (
              <div className="text-[18px] tracking-wide">START YOUR DESIGN BELOW</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const getLoadingContent = () => {
    if (type === "generating") {
      return {
        message: message || "Creating your designs..."
      };
    }
    if (type === "refining") {
      return {
        message: message || "Refining your design..."
      };
    }
    if (type === "uploading") {
      return {
        message: message || "Uploading image...",
        submessage: submessage || "Preparing for DTF artwork"
      };
    }
    return {
      message: message || "Loading...",
      submessage: submessage || "Please wait"
    };
  };

  const content = getLoadingContent();

  return (
    <div className="flex min-h-[300px] items-center justify-center">
      <div className="flex-1 text-center">
        <Loader2
          className={`${isMobile ? "h-10 w-10" : "h-12 w-12"} mx-auto mb-4 animate-spin text-indigo-600`}
        />
        <p className={`text-gray-700 ${isMobile ? "text-base" : "text-lg"}`}>{content.message}</p>
      </div>
    </div>
  );
};
